import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBriefcase,
  FaChevronDown,
  FaHome,
  FaHotel,
  FaMapMarkerAlt,
  FaPlus,
} from 'react-icons/fa';
import DeliveryDistanceMeta from '../../components/DeliveryDistanceMeta/DeliveryDistanceMeta';
import AddressFormModal from '../Account/Addresses/AddressFormModal';
import { canAddSavedAddress } from '../Account/Addresses/addressManagementUtils';
import { buildAuthRequiredRedirectUrl } from '../Account/profileFormUtils';
import { sortAddressesForCheckout } from '../Checkout/checkoutAddressUtils';

const ADDRESS_TYPES = [
  { value: 0, label: 'Home', icon: FaHome },
  { value: 1, label: 'Work', icon: FaBriefcase },
  { value: 2, label: 'Hotel', icon: FaHotel },
  { value: 3, label: 'Other', icon: FaMapMarkerAlt },
];

function addressTypeMeta(type) {
  return ADDRESS_TYPES.find((item) => item.value === Number(type)) || ADDRESS_TYPES[3];
}

function formatAddressLines(address) {
  return [address.address, address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(', ');
}

function hasMapLocation(address) {
  return Number.isFinite(address.latitude) && Number.isFinite(address.longitude);
}

/**
 * Inline delivery address switcher for cart order summary.
 *
 * @param {{
 *   status: 'idle' | 'loading' | 'ready' | 'guest',
 *   addresses: Array<object>,
 *   selectedId: number | null,
 *   selectedAddress: object | null,
 *   onSelect: (id: number) => void,
 *   onReload: () => void,
 * }} props
 */
const CartDeliveryAddressPicker = ({
  status,
  addresses,
  selectedId,
  selectedAddress,
  onSelect,
  onReload,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [addressFormOpen, setAddressFormOpen] = useState(false);

  const sorted = sortAddressesForCheckout(addresses);
  const canAddAddress = canAddSavedAddress(addresses.length);

  useEffect(() => {
    if (status === 'ready' && !selectedAddress && addresses.length > 0) {
      setExpanded(true);
    }
  }, [status, selectedAddress, addresses.length]);

  const handleSaved = (address) => {
    setAddressFormOpen(false);
    onReload();
    const id = address?.id != null ? Number(address.id) : null;
    if (id != null) onSelect(id);
  };

  if (status === 'guest') {
    return (
      <div className="cart-address-picker cart-summary-panel">
        <p className="cart-summary-setup__heading">Delivery address</p>
        <p className="cart-address-picker__hint">
          <Link to={buildAuthRequiredRedirectUrl('/cart')}>Sign in</Link> to choose a saved address
          and see delivery charges.
        </p>
      </div>
    );
  }

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="cart-address-picker cart-summary-panel" aria-busy="true">
        <p className="cart-summary-setup__heading">Delivery address</p>
        <p className="cart-address-picker__hint">Loading saved addresses…</p>
      </div>
    );
  }

  const summaryLabel = selectedAddress?.name || 'Choose address';
  const summaryLine = selectedAddress
    ? formatAddressLines(selectedAddress)
    : 'Select an address with map location for delivery pricing';
  return (
    <div className="cart-address-picker cart-summary-panel">
      <p className="cart-summary-setup__heading">Delivery address</p>

      <AddressFormModal
        open={addressFormOpen}
        onClose={() => setAddressFormOpen(false)}
        onSaved={handleSaved}
        addressesCount={addresses.length}
        saveLabel="Save address"
      />

      <button
        type="button"
        className={`cart-address-picker__summary${expanded ? ' cart-address-picker__summary--open' : ''}${selectedAddress ? '' : ' cart-address-picker__summary--prompt'}`}
        aria-expanded={expanded}
        aria-controls="cart-address-picker-list"
        onClick={() => setExpanded((open) => !open)}
      >
        <span className="cart-address-picker__summary-icon" aria-hidden>
          <FaMapMarkerAlt />
        </span>
        <span className="cart-address-picker__summary-text">
          <span className="cart-address-picker__summary-label">
            {selectedAddress ? 'Delivering to' : 'Deliver to'}
          </span>
          <strong>{summaryLabel}</strong>
          <span className="cart-address-picker__summary-line">{summaryLine}</span>
          {selectedAddress && hasMapLocation(selectedAddress) ? (
            <DeliveryDistanceMeta address={selectedAddress} />
          ) : null}
        </span>
        <span className="cart-address-picker__summary-action">
          {expanded ? 'Close' : 'Change'}
          <FaChevronDown aria-hidden />
        </span>
      </button>

      {expanded ? (
        <div id="cart-address-picker-list" className="cart-address-picker__list" role="list">
          {sorted.map((address) => {
            const meta = addressTypeMeta(address.type);
            const Icon = meta.icon;
            const selected = Number(address.id) === Number(selectedId);
            const deliverable = hasMapLocation(address);

            return (
              <article
                key={address.id}
                role="listitem"
                className={`cart-address-tile${selected ? ' cart-address-tile--selected' : ''}${!deliverable ? ' cart-address-tile--incomplete' : ''}`}
              >
                <div className="cart-address-tile__head">
                  <span className="cart-address-tile__icon">
                    <Icon aria-hidden />
                  </span>
                  <div className="cart-address-tile__labels">
                    <strong>{address.name || meta.label}</strong>
                    {address.isPrimary ? (
                      <span className="cart-address-tile__badge">Default</span>
                    ) : null}
                  </div>
                </div>
                <p className="cart-address-tile__lines">{formatAddressLines(address)}</p>
                {deliverable ? <DeliveryDistanceMeta address={address} /> : null}
                {!deliverable ? (
                  <small className="cart-address-tile__warn">
                    Missing map location —{' '}
                    <Link to="/account/addresses">update in My Addresses</Link>
                  </small>
                ) : null}
                <button
                  type="button"
                  className={`cart-address-tile__cta${selected ? ' cart-address-tile__cta--selected' : ''}`}
                  disabled={!deliverable}
                  onClick={() => {
                    onSelect(Number(address.id));
                    setExpanded(false);
                  }}
                >
                  {selected ? 'Delivering here' : 'Deliver here'}
                </button>
              </article>
            );
          })}

          {canAddAddress ? (
            <button
              type="button"
              className="cart-address-tile cart-address-tile--add"
              onClick={() => {
                setAddressFormOpen(true);
                setExpanded(false);
              }}
            >
              <FaPlus aria-hidden />
              <span>Add new address</span>
            </button>
          ) : null}
        </div>
      ) : null}

      {!expanded && sorted.length === 0 ? (
        <p className="cart-address-picker__hint">
          No saved addresses yet.{' '}
          <button
            type="button"
            className="cart-address-picker__link-btn"
            onClick={() => setAddressFormOpen(true)}
          >
            Add an address
          </button>{' '}
          to calculate delivery and tax.
        </p>
      ) : null}
    </div>
  );
};

export default CartDeliveryAddressPicker;
